import { useState } from "react";
import { X, RefreshCw, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "@/hooks/use-toast";
import {
  useGetProhibitedKeywords,
  useDeleteProhibitedKeyword,
  usePopulateCompetitorBrands,
} from "@/resources/prohibited-keyword/prohibited-keyword.api";
import { ConfirmDeleteDialog } from "@/components/shared/ConfirmDeleteDialog";
import { AddProhibitedKeywordsForm } from "@/components/brand-context/AddProhibitedKeywordsForm";
import { useAppData } from "@/contexts/AppDataContext";

interface ProhibitedKeywordsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  brandId: number | null;
}

export function ProhibitedKeywordsModal({ open, onOpenChange, brandId }: ProhibitedKeywordsModalProps) {
  const [keywordToRemove, setKeywordToRemove] = useState<{ id: number; keyword: string; source: "competitor" | "general" } | null>(null);

  const { brands } = useAppData();
  const brandLabel = brands.find((b) => b.id === brandId)?.name ?? "this brand";

  const { data: keywords = [], isLoading } = useGetProhibitedKeywords(brandId);
  const deleteMutation = useDeleteProhibitedKeyword();
  const populateMutation = usePopulateCompetitorBrands();

  // AUT-220: the confirm dialog closes as soon as the request is fired instead of
  // waiting for it to settle. The delete is already optimistic, and the server work
  // behind it (rebuilding search terms for the brand -- platform-wide for the global
  // tier) can run for minutes, which used to hold the dialog open and keep every
  // remove button disabled: "I'm not able to delete any other keywords until I
  // refresh the page".
  const handleRemove = () => {
    if (!brandId || !keywordToRemove) return;
    const { id, keyword } = keywordToRemove;
    setKeywordToRemove(null);
    deleteMutation.mutate(
      { id, brandId },
      {
        onSuccess: () =>
          toast({
            title: "Removed",
            description: `"${keyword}" removed from prohibited keywords.`,
          }),
      },
    );
  };

  const handlePopulateCompetitors = () => {
    if (!brandId) return;

    populateMutation.mutate(
      { brand_id: brandId },
      {
        onSuccess: (result) => {
          const removedMsg = result.removed_from_search_terms?.modified_entries
            ? ` Removed from ${result.removed_from_search_terms.modified_entries} search term entries.`
            : "";
          toast({
            title: "Competitor Brands Updated",
            description: `${result.inserted} new competitor brand${result.inserted !== 1 ? "s" : ""} added.${removedMsg}`,
          });
        },
      },
    );
  };

  const competitorBrands = keywords.filter((k) => k.origin === "competitor_brand");
  const generalProhibited = keywords.filter((k) => k.origin !== "competitor_brand");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-card">
        <DialogHeader>
          <DialogTitle>Prohibited Keywords</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          Keywords listed here will never appear in generated search terms. Competitor brand names are automatically included.
        </p>

        <AddProhibitedKeywordsForm
          brandId={brandId}
          existingKeywords={keywords}
          singleInputPlaceholder="Add a prohibited keyword..."
        />

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <ScrollArea className="max-h-[300px]">
            <div className="space-y-4 pr-3">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Competitor Brands
                  </h4>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs"
                    onClick={handlePopulateCompetitors}
                    disabled={populateMutation.isPending}
                  >
                    {populateMutation.isPending ? (
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    ) : (
                      <RefreshCw className="h-3 w-3 mr-1" />
                    )}
                    {competitorBrands.length === 0 ? "Detect from Landscapes" : "Refresh"}
                  </Button>
                </div>
                {competitorBrands.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No competitor brands detected yet.</p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {competitorBrands.map((kw) => (
                      <Badge key={kw.id} className="text-xs gap-1 pr-1 bg-destructive/15 text-destructive border-destructive/30 hover:bg-destructive/20">
                        {kw.keyword}
                        <button
                          onClick={() => setKeywordToRemove({ id: kw.id, keyword: kw.keyword, source: "competitor" })}
                          disabled={deleteMutation.isPending && deleteMutation.variables?.id === kw.id}
                          className="ml-1 rounded-full hover:bg-destructive-foreground/20 p-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {generalProhibited.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                    General Prohibited
                  </h4>
                  <div className="flex flex-wrap gap-1.5">
                    {generalProhibited.map((kw) => (
                      <Badge key={kw.id} className="text-xs gap-1 pr-1 bg-destructive/15 text-destructive border-destructive/30 hover:bg-destructive/20">
                        {kw.keyword}
                        <button
                          onClick={() => setKeywordToRemove({ id: kw.id, keyword: kw.keyword, source: "general" })}
                          disabled={deleteMutation.isPending && deleteMutation.variables?.id === kw.id}
                          className="ml-1 rounded-full hover:bg-muted p-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>
        )}

        <div className="text-xs text-muted-foreground pt-2 border-t border-border">
          {keywords.length} prohibited keyword{keywords.length !== 1 ? "s" : ""} total
        </div>

        <ConfirmDeleteDialog
          open={!!keywordToRemove}
          onOpenChange={(o) => { if (!o) setKeywordToRemove(null); }}
          title={
            keywordToRemove?.source === "competitor"
              ? `Remove "${keywordToRemove?.keyword}" from ${brandLabel}?`
              : `Remove "${keywordToRemove?.keyword}" from prohibited keywords?`
          }
          description="This keyword will be allowed in generated search terms again."
          confirmLabel="Remove"
          onConfirm={handleRemove}
        />
      </DialogContent>
    </Dialog>
  );
}
